/**
 * Cupom de desconto no carrinho deslizante.
 *
 * O markup vive dentro de `#CartDrawer`, que o tema reescreve (innerHTML) a cada atualização do
 * carrinho; por isso a instância é recriada a cada render e os listeners são presos ao próprio
 * elemento no constructor.
 *
 * Fluxo: POST /cart/update.js com `discount` (lista de códigos separada por vírgula, substitui a
 * lista atual) + `sections` para já receber o drawer renderizado.
 */
if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      constructor() {
        super();

        this.drawer = this.closest('cart-drawer');
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="discount"]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.errorElement = this.querySelector('.cart-discount__error');

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.input.addEventListener('input', () => this.showError(''));
        this.addEventListener('click', (event) => {
          const removeButton = event.target.closest('.cart-discount__remove');
          if (removeButton) this.onRemove(removeButton.dataset.code);
        });
      }

      /** @returns {string[]} códigos já aplicados (lidos das pílulas renderizadas pelo Liquid) */
      get appliedCodes() {
        return [...this.querySelectorAll('[data-discount-code]')].map((el) => el.dataset.discountCode);
      }

      onSubmit(event) {
        event.preventDefault();

        const code = this.input.value.trim();
        if (!code || this.isLoading) return;

        const applied = this.appliedCodes;
        if (applied.some((appliedCode) => appliedCode.toLowerCase() === code.toLowerCase())) {
          this.input.value = '';
          return;
        }

        this.setLoading(true);
        this.updateDiscounts([...applied, code])
          .then((cart) => {
            const rejected = (cart.discount_codes || []).some(
              (discount) => discount.code.toLowerCase() === code.toLowerCase() && !discount.applicable
            );

            if (rejected) {
              // Remove o código inválido do carrinho; sem render (o drawer segue como está).
              this.updateDiscounts(applied, false).catch(() => {});
              this.showError(this.dataset.errorInvalid);
              return;
            }

            this.render(cart.sections);
          })
          .catch(() => this.showError(this.dataset.errorNetwork))
          .finally(() => this.setLoading(false));
      }

      onRemove(code) {
        if (this.isLoading) return;

        const remaining = this.appliedCodes.filter((appliedCode) => appliedCode !== code);

        this.setLoading(true);
        this.updateDiscounts(remaining)
          .then((cart) => this.render(cart.sections))
          .catch(() => this.showError(this.dataset.errorNetwork))
          .finally(() => this.setLoading(false));
      }

      /**
       * @param {string[]} codes - lista final de cupons do carrinho
       * @param {boolean} [withSections] - pede o HTML renderizado das sections do drawer
       * @returns {Promise<object>} carrinho (JSON do /cart/update.js)
       */
      updateDiscounts(codes, withSections = true) {
        const payload = { discount: codes.join(',') };
        if (withSections) {
          payload.sections = this.drawer.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        return fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify(payload) }).then((response) => {
          if (!response.ok) throw new Error(`cart/update.js ${response.status}`);
          return response.json();
        });
      }

      render(sections) {
        // `this` é descartado pelo render; o drawer é mantido aberto e não dispara o feedback de "adicionado".
        this.drawer.refreshSections({ sections });
      }

      setLoading(isLoading) {
        this.isLoading = isLoading;
        this.submitButton.toggleAttribute('aria-disabled', isLoading);
        this.classList.toggle('is-loading', isLoading);
        this.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        this.errorElement.textContent = message;
        this.errorElement.toggleAttribute('hidden', !message);
        this.input.toggleAttribute('aria-invalid', Boolean(message));
      }
    }
  );
}
